import { useState, useRef, useEffect } from "react";

/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — Reveal
   The "a gift is waiting" page. Opened from the email link
   AFTER you've hand-curated the design in Atelier.

   FLOW:  threshold ("Open it") → reveal (spinning model + name)
          → story → choose finish (live material swap) → close ($149)

   HOW IT'S FED:
   - Email link looks like:  yourstruly.gift/reveal?t=ORDER_TOKEN
   - On load it calls  GET /api/reveal?t=TOKEN  and expects:
       { recipient_name, giver_name?, sculpt_name, sculpt_story,
         art_url, model_url }
     (these map straight onto what Atelier already writes to Supabase:
      sculpt_name, sculpt_desc, sculpt_story, art_url + the curated model)
   - If no token / fetch fails, it falls back to DEMO_DATA so you can
     preview the page. Delete DEMO_DATA before launch if you want.

   ROUTER:
     // App.jsx
     import Reveal from "./Reveal";
     if (path === "/reveal") return <Reveal />;
   ═══════════════════════════════════════════════════════════ */

const REVEAL_API = "/api/reveal";                 // you build this endpoint
const STRIPE_SCULPTURE = "https://buy.stripe.com/3cIfZh5ojbXEgih9JC18c01";
const STRIPE_PRINT_FRAMED = "https://buy.stripe.com/3cIfZh5ojbXEgih9JC18c01";
const STRIPE_PRINT_UNFRAMED = "https://buy.stripe.com/3cIfZh5ojbXEgih9JC18c01";

/* preview-only fallback so the page renders with no backend */
const DEMO_DATA = {
  recipient_name: "Maya",
  giver_name: null,
  sculpt_name: "The Weight She Carried Lightly",
  sculpt_story:
    "She never told you how heavy it was — she just kept moving, kept laughing, kept showing up. Here that strength has a shape: rising, unbreakable, lit from somewhere deep inside. This is Maya as the people who love her already see her.",
  art_url: null,
  model_url: null, // drop a real .glb URL here to see the spinning model in preview
};

/* ─── theme (matches AppV2 / AppV3) ─── */
const GOLD = "#c9a96e";
const GOLD_SOFT = "rgba(201,169,110,0.55)";
const TEXT = "#e8e6e2";
const TEXT_SOFT = "rgba(232,230,226,0.65)";
const TEXT_MUTED = "rgba(200,200,220,0.40)";
const LINE = "rgba(201,169,110,0.15)";
const SERIF = '"Cormorant Garamond",serif';
const SANS = '"Outfit",sans-serif';

/* customer-facing finishes → your RESIN_MATERIALS presets */
const FINISHES = [
  { key: "resin",  label: "Obsidian Black", swatch: "#0a0a0e" },
  { key: "steel",  label: "Golden Bronze",  swatch: "#d4a855" },
  { key: "bronze", label: "Pearl White",    swatch: "#f0ece2" },
];

/* ═══════════════════════════════════════════════════════════
   PORTED FROM AppV2 — script loader, env map, materials, ModelViewer
   (unchanged so the model looks identical to your other surfaces)
   ═══════════════════════════════════════════════════════════ */
function loadScript(src, globalCheck) {
  return new Promise((resolve, reject) => {
    if (globalCheck && globalCheck()) return resolve();
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { const poll = setInterval(() => { if (globalCheck && globalCheck()) { clearInterval(poll); resolve(); } }, 50); setTimeout(() => { clearInterval(poll); resolve(); }, 5000); return; }
    const s = document.createElement("script"); s.src = src;
    s.onload = () => { if (globalCheck) { const poll = setInterval(() => { if (globalCheck()) { clearInterval(poll); resolve(); } }, 50); setTimeout(() => { clearInterval(poll); resolve(); }, 5000); } else resolve(); };
    s.onerror = reject; document.head.appendChild(s);
  });
}

let _cachedEnvMap = null, _envMapPromise = null;
function buildStudioEnvMap(THREE, renderer) {
  const pmremGen = new THREE.PMREMGenerator(renderer);
  const canvas = document.createElement("canvas"); canvas.width = 2048; canvas.height = 1024;
  const ctx = canvas.getContext("2d");
  const bg = ctx.createLinearGradient(0, 0, 0, 1024);
  bg.addColorStop(0, "#252530"); bg.addColorStop(0.5, "#141418"); bg.addColorStop(1, "#0c0c10");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, 2048, 1024);
  ctx.fillStyle = "rgba(255,252,245,1.0)"; ctx.fillRect(950, 0, 700, 500);
  ctx.fillStyle = "rgba(255,250,240,0.8)"; ctx.fillRect(750, 0, 1000, 400);
  ctx.fillStyle = "rgba(215,230,255,0.9)"; ctx.fillRect(30, 30, 550, 450);
  ctx.fillStyle = "rgba(255,255,255,1.0)"; ctx.fillRect(730, 10, 90, 460);
  const tex = new THREE.CanvasTexture(canvas);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  const envMap = pmremGen.fromEquirectangular(tex).texture;
  tex.dispose(); pmremGen.dispose();
  return envMap;
}
function loadStudioEnvMap(THREE, renderer) {
  if (_cachedEnvMap) return Promise.resolve(_cachedEnvMap);
  if (_envMapPromise) return _envMapPromise;
  _envMapPromise = new Promise((resolve) => { const e = buildStudioEnvMap(THREE, renderer); _cachedEnvMap = e; resolve(e); });
  return _envMapPromise;
}

const RESIN_MATERIALS = {
  resin:  { color: 0x020204, roughness: 0.25, metalness: 0.75, envMapIntensity: 0.6 },
  steel:  { color: 0xd4a855, roughness: 0.12, metalness: 0.95, envMapIntensity: 1.5 },
  bronze: { color: 0xf8f4ec, roughness: 0.30, metalness: 0.20, envMapIntensity: 0.7 },
};

function ModelViewer({ url, style, material = "resin" }) {
  const mountRef = useRef(null); const sceneDataRef = useRef(null);
  const [loading, setLoading] = useState(true); const [error, setError] = useState(false);

  useEffect(() => {
    if (!url || !mountRef.current) return; let cancelled = false; let animId; setLoading(true); setError(false);
    sceneDataRef.current = null;
    (async () => {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js", () => !!window.THREE); if (cancelled) return;
      await loadScript("https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js", () => !!window.THREE.GLTFLoader); if (cancelled || !mountRef.current) return;
      const THREE = window.THREE; if (!THREE?.GLTFLoader) { setError(true); setLoading(false); return; }
      const container = mountRef.current; const w = container.clientWidth, h = container.clientHeight || 400;
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(35, w / h, 0.01, 100); camera.position.set(0, 0.8, 3); camera.lookAt(0, 0.2, 0);
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
      renderer.setSize(w, h); renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.outputEncoding = THREE.sRGBEncoding; renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 2.2;
      renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      container.innerHTML = ""; container.appendChild(renderer.domElement);

      const envMap = await loadStudioEnvMap(THREE, renderer); if (cancelled) return;
      scene.environment = envMap;

      scene.add(new THREE.HemisphereLight(0xfff8f0, 0x181820, 0.4));
      const keyLight = new THREE.SpotLight(0xfff0d4, 5.0, 16, Math.PI / 5, 0.3, 0.7); keyLight.position.set(3, 6, 3); keyLight.castShadow = true; keyLight.shadow.mapSize.set(2048, 2048); scene.add(keyLight);
      const fillLight = new THREE.DirectionalLight(0xd4e0ff, 1.0); fillLight.position.set(-4, 3, 2); scene.add(fillLight);
      const rimLight = new THREE.SpotLight(0xffeedd, 3.0, 14, Math.PI / 4, 0.5, 0.7); rimLight.position.set(-2, 5, -4); scene.add(rimLight);
      const accentLight = new THREE.PointLight(0xc9a96e, 0.35, 5); accentLight.position.set(0, -0.3, 1); scene.add(accentLight);

      const pedestal = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.65, 0.08, 64), new THREE.MeshStandardMaterial({ color: 0x0c0c12, roughness: 0.08, metalness: 0.9, envMap, envMapIntensity: 0.8 }));
      pedestal.position.set(0, -0.5, 0); pedestal.receiveShadow = true; scene.add(pedestal);
      const ringMat = new THREE.MeshStandardMaterial({ color: 0xc9a96e, roughness: 0.12, metalness: 0.95, emissive: 0xc9a96e, emissiveIntensity: 0.1, envMap, envMapIntensity: 1.0 });
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.58, 0.008, 12, 64), ringMat); ring.rotation.x = Math.PI / 2; ring.position.set(0, -0.46, 0); scene.add(ring);
      const floor = new THREE.Mesh(new THREE.PlaneGeometry(8, 8), new THREE.MeshStandardMaterial({ color: 0x0a0a10, roughness: 0.05, metalness: 0.95, envMap, envMapIntensity: 0.4 }));
      floor.rotation.x = -Math.PI / 2; floor.position.y = -0.54; floor.receiveShadow = true; scene.add(floor);

      let model = null; const loader = new THREE.GLTFLoader();
      const loadUrl = url.startsWith("/") ? url : "/api/model-proxy?url=" + encodeURIComponent(url);
      loader.load(loadUrl, (gltf) => {
        if (cancelled) return; model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model); const size = box.getSize(new THREE.Vector3());
        model.scale.setScalar(1.2 / Math.max(size.x, size.y, size.z));
        const box2 = new THREE.Box3().setFromObject(model); const c2 = box2.getCenter(new THREE.Vector3());
        model.position.x = -c2.x; model.position.z = -c2.z; model.position.y = -box2.min.y - 0.47;
        const preset = RESIN_MATERIALS[material] || RESIN_MATERIALS.resin;
        model.traverse(ch => { if (ch.isMesh) { ch.castShadow = true; ch.receiveShadow = true; ch.material = new THREE.MeshStandardMaterial({ color: preset.color, roughness: preset.roughness, metalness: preset.metalness, envMap, envMapIntensity: preset.envMapIntensity }); } });
        scene.add(model); keyLight.target = model; scene.add(keyLight.target);
        sceneDataRef.current = { model, envMap, THREE }; setLoading(false);
      }, undefined, () => { setError(true); setLoading(false); });

      let mouseX = 0, mouseY = 0, isDragging = false, rotY = 0, rotX = 0, autoRotate = true;
      const onDown = e => { isDragging = true; autoRotate = false; mouseX = e.clientX || e.touches?.[0]?.clientX; mouseY = e.clientY || e.touches?.[0]?.clientY; };
      const onMove = e => { if (!isDragging) return; const x = e.clientX || e.touches?.[0]?.clientX; const y = e.clientY || e.touches?.[0]?.clientY; rotY += (x - mouseX) * 0.008; rotX += (y - mouseY) * 0.005; rotX = Math.max(-0.5, Math.min(0.5, rotX)); mouseX = x; mouseY = y; };
      const onUp = () => { isDragging = false; setTimeout(() => { autoRotate = true; }, 3000); };
      container.addEventListener("mousedown", onDown); container.addEventListener("mousemove", onMove); container.addEventListener("mouseup", onUp); container.addEventListener("mouseleave", onUp);
      container.addEventListener("touchstart", onDown, { passive: true }); container.addEventListener("touchmove", onMove, { passive: true }); container.addEventListener("touchend", onUp);

      const animate = () => { animId = requestAnimationFrame(animate); if (autoRotate) rotY += 0.003; if (model) { model.rotation.y = rotY; model.rotation.x = rotX; } ringMat.emissiveIntensity = 0.1 + Math.sin(Date.now() * 0.0015) * 0.05; renderer.render(scene, camera); }; animate();
      const onResize = () => { if (!container) return; const nw = container.clientWidth, nh = container.clientHeight || 400; camera.aspect = nw / nh; camera.updateProjectionMatrix(); renderer.setSize(nw, nh); }; window.addEventListener("resize", onResize);
      mountRef.current._cleanup = () => { cancelAnimationFrame(animId); window.removeEventListener("resize", onResize); ["mousedown","mousemove","mouseup","mouseleave","touchstart","touchmove","touchend"].forEach(ev => container.removeEventListener(ev, ev.startsWith("touch") ? (ev==="touchstart"?onDown:ev==="touchmove"?onMove:onUp) : (ev==="mousedown"?onDown:ev==="mousemove"?onMove:onUp))); renderer.dispose(); };
    })();
    return () => { cancelled = true; if (mountRef.current?._cleanup) mountRef.current._cleanup(); };
  }, [url]);

  /* live finish swap — no reload */
  useEffect(() => {
    const sd = sceneDataRef.current; if (!sd || !sd.model) return;
    const { model, envMap, THREE } = sd; const preset = RESIN_MATERIALS[material] || RESIN_MATERIALS.resin;
    model.traverse(ch => { if (ch.isMesh) { ch.material = new THREE.MeshStandardMaterial({ color: preset.color, roughness: preset.roughness, metalness: preset.metalness, envMap, envMapIntensity: preset.envMapIntensity }); ch.material.needsUpdate = true; } });
  }, [material]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative", borderRadius: 16, overflow: "hidden", ...style }}>
      <div ref={mountRef} style={{ width: "100%", height: "100%", cursor: "grab" }} />
      {loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(12,11,16,0.7)", backdropFilter: "blur(8px)" }}><div style={{ fontSize: 11, fontFamily: SERIF, fontStyle: "italic", color: GOLD_SOFT }}>Loading…</div></div>}
      {error && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ fontSize: 11, color: "rgba(201,169,110,0.4)" }}>3D preview unavailable</div></div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLES (fonts + keyframes injected once)
   ═══════════════════════════════════════════════════════════ */
function useRevealStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-yt-fonts]")) {
      const l = document.createElement("link"); l.rel = "stylesheet"; l.dataset.ytFonts = "1";
      l.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(l);
    }
    if (!document.querySelector("style[data-yt-reveal]")) {
      const s = document.createElement("style"); s.dataset.ytReveal = "1";
      s.textContent = `
        @keyframes ytFadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
        @keyframes ytScaleIn{from{opacity:0;transform:scale(0.92)}to{opacity:1;transform:scale(1)}}
        @keyframes ytBreathe{0%,100%{opacity:0.5}50%{opacity:1}}
        .yt-cta{transition:transform .2s, box-shadow .2s}
        .yt-cta:hover{transform:translateY(-2px);box-shadow:0 16px 44px -8px rgba(201,169,110,.6)!important}
      `;
      document.head.appendChild(s);
    }
  }, []);
}

/* ═══════════════════════════════════════════════════════════
   THE PAGE
   ═══════════════════════════════════════════════════════════ */
export default function Reveal() {
  useRevealStyles();
  const [opened, setOpened] = useState(false);     // beat 0 → 1
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [finish, setFinish] = useState("resin");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t") || params.get("order");
    if (!token) { setData(DEMO_DATA); setLoadState("ready"); return; }
    (async () => {
      try {
        const r = await fetch(`${REVEAL_API}?t=${encodeURIComponent(token)}`);
        if (!r.ok) throw new Error("not ready");
        const d = await r.json();
        setData(d); setLoadState("ready");
      } catch {
        setData(DEMO_DATA); setLoadState("ready"); // swap to setLoadState("error") in prod if you prefer
      }
    })();
  }, []);

  const bg = "radial-gradient(ellipse 90% 70% at 50% 0%, #15131c 0%, #0c0b10 55%, #060509 100%)";
  const wrap = { minHeight: "100vh", color: TEXT, fontFamily: SANS, background: bg, position: "relative", overflowX: "hidden" };

  const cta = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%",
    padding: "18px 30px", borderRadius: 14, border: "none", cursor: "pointer", textDecoration: "none",
    fontFamily: SANS, fontWeight: 700, fontSize: 16, letterSpacing: "0.04em", color: "#0a0a14",
    background: "linear-gradient(135deg,#b9924f 0%,#e9d3a0 50%,#b9924f 100%)",
    boxShadow: "0 12px 34px -8px rgba(201,169,110,0.5)",
  };

  if (loadState === "loading") {
    return <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: SERIF, fontStyle: "italic", color: GOLD_SOFT, fontSize: 16, animation: "ytBreathe 2s ease-in-out infinite" }}>Unwrapping…</div>
    </div>;
  }

  const name = data?.recipient_name || "them";
  const modelUrl = data?.model_url;

  /* ── BEAT 0: THRESHOLD ── */
  if (!opened) {
    return (
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 420, animation: "ytFadeUp 0.9s ease" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD_SOFT, marginBottom: 22 }}>Yours Truly</div>
          <div style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, fontSize: 34, lineHeight: 1.2, marginBottom: 14 }}>
            Something was made for {name}.
          </div>
          <p style={{ color: TEXT_SOFT, fontSize: 15, marginBottom: 34 }}>
            One sculpture, designed from everything you shared. It exists nowhere else.
          </p>
          <button className="yt-cta" style={{ ...cta, width: "auto" }} onClick={() => setOpened(true)}>Open it →</button>
        </div>
      </div>
    );
  }

  /* ── BEATS 1–5: REVEAL ── */
  return (
    <div style={wrap}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px 80px" }}>

        {/* header */}
        <div style={{ textAlign: "center", marginBottom: 14, animation: "ytFadeUp 0.6s ease" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: TEXT_MUTED }}>For {name} · One of one</div>
        </div>

        {/* BEAT 1 — name + model */}
        <h1 style={{ textAlign: "center", fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, fontSize: 34, lineHeight: 1.15, margin: "0 0 22px", animation: "ytFadeUp 0.7s ease 0.1s both" }}>
          {data?.sculpt_name || "Your Sculpture"}
        </h1>

        <div style={{ aspectRatio: "1 / 1", maxWidth: 460, margin: "0 auto", borderRadius: 20, overflow: "hidden", border: "1px solid " + LINE, boxShadow: "0 30px 90px -30px rgba(0,0,0,0.9)", animation: "ytScaleIn 0.9s ease 0.2s both" }}>
          {modelUrl
            ? <ModelViewer url={modelUrl} material={finish} />
            : data?.art_url
              ? <img src={data.art_url} alt={data?.sculpt_name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: "radial-gradient(ellipse at 50% 35%, rgba(201,169,110,0.12), transparent 65%), linear-gradient(160deg,#15131c,#0a0910)" }}>
                  <div style={{ fontFamily: SERIF, fontStyle: "italic", color: GOLD_SOFT, fontSize: 16, textAlign: "center", padding: "0 28px" }}>Spinning 3D model renders here</div>
                  <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: TEXT_MUTED }}>pass model_url to see it live</div>
                </div>}
        </div>
        {modelUrl && <div style={{ textAlign: "center", marginTop: 10, fontSize: 10, color: TEXT_MUTED, letterSpacing: "0.1em" }}>Drag to turn it</div>}

        {/* BEAT 2 — story */}
        {data?.sculpt_story && (
          <div style={{ maxWidth: 520, margin: "32px auto 0", textAlign: "center", animation: "ytFadeUp 0.7s ease 0.35s both" }}>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 19, lineHeight: 1.65, color: TEXT, margin: 0 }}>
              {data.sculpt_story}
            </p>
          </div>
        )}

        {/* BEAT 3 — finishes (live swap) */}
        <div style={{ maxWidth: 520, margin: "44px auto 0", animation: "ytFadeUp 0.7s ease 0.5s both" }}>
          <div style={{ textAlign: "center", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: GOLD_SOFT, marginBottom: 16 }}>Choose how it's cast</div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            {FINISHES.map(f => {
              const sel = finish === f.key;
              return (
                <button key={f.key} onClick={() => setFinish(f.key)} style={{
                  flex: 1, maxWidth: 150, padding: "14px 10px", borderRadius: 14, cursor: "pointer",
                  background: sel ? "rgba(201,169,110,0.10)" : "rgba(255,255,255,0.02)",
                  border: sel ? "1.5px solid " + GOLD : "1px solid " + LINE,
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 9, transition: "all 0.2s",
                }}>
                  <span style={{ width: 30, height: 30, borderRadius: "50%", background: f.swatch, border: "1px solid rgba(255,255,255,0.2)", boxShadow: sel ? "0 0 0 3px rgba(201,169,110,0.25)" : "none" }} />
                  <span style={{ fontSize: 12, color: sel ? TEXT : TEXT_SOFT, fontWeight: sel ? 500 : 400, fontFamily: SANS }}>{f.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* BEAT 4 — the close */}
        <div style={{ maxWidth: 460, margin: "44px auto 0", textAlign: "center", animation: "ytFadeUp 0.7s ease 0.65s both" }}>
          <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 16, color: GOLD_SOFT, lineHeight: 1.6, margin: "0 0 8px" }}>
            This was designed only for {name}. It exists nowhere else.
          </p>
          <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 26, color: TEXT, margin: "16px 0 6px" }}>$149</div>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 22 }}>Handcrafted to order in Atlanta · Ships in 2–3 weeks</div>

          <a className="yt-cta" style={cta} href={STRIPE_SCULPTURE} target="_blank" rel="noopener noreferrer">
            Make it real — $149 →
          </a>

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16, padding: "8px 16px", borderRadius: 999, border: "1px solid " + LINE, background: "rgba(201,169,110,0.05)" }}>
            <span style={{ color: GOLD, fontFamily: SERIF, fontStyle: "italic" }}>♡</span>
            <span style={{ fontSize: 12, color: TEXT_SOFT }}>Love it, or we remake it — free.</span>
          </div>
        </div>

        {/* BEAT 5 — soft landing */}
        <div style={{ maxWidth: 460, margin: "40px auto 0", paddingTop: 26, borderTop: "1px solid rgba(255,255,255,0.05)", textAlign: "center", animation: "ytFadeUp 0.7s ease 0.8s both" }}>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 14, letterSpacing: "0.04em" }}>Not ready for the full piece? Start with the art.</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <a href={STRIPE_PRINT_FRAMED} target="_blank" rel="noopener noreferrer" style={{ padding: "11px 18px", borderRadius: 10, border: "1px solid " + LINE, color: TEXT_SOFT, fontSize: 13, textDecoration: "none", background: "rgba(201,169,110,0.05)" }}>Framed print — $29</a>
            <a href={STRIPE_PRINT_UNFRAMED} target="_blank" rel="noopener noreferrer" style={{ padding: "11px 18px", borderRadius: 10, border: "1px solid " + LINE, color: TEXT_MUTED, fontSize: 13, textDecoration: "none", background: "transparent" }}>Unframed — $29</a>
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 44, fontSize: 11, color: TEXT_MUTED, fontFamily: SERIF, fontStyle: "italic", letterSpacing: "0.1em" }}>yourstruly.gift</div>
      </div>
    </div>
  );
}
